'use client'

import type { ReactNode } from 'react'

import { createContext, useContext } from 'react'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Tabs. Figma: 10 / Components / Tabs.
 * variant segmented: TabsList on Colors/action/segment/track (6px corners, 4px padding); the active
 *   trigger fills with Colors/action/segment/active. Labels and counts are Roboto / text-sm / medium.
 * variant icon: page-level navigation with a 20px icon and Roboto / text-base / medium label; the active
 *   trigger sits on a soft vertical gradient from Colors/surface/muted to Colors/surface/raised.
 */
type TabsVariant = 'segmented' | 'icon'

const TabsVariantContext = createContext<TabsVariant>('segmented')

const Tabs = TabsPrimitive.Root

type TabsListProps = Omit<TabsPrimitive.List.Props, 'className'> & {
    className?: string
    variant?: TabsVariant
    fullWidth?: boolean
}

function TabsList({ className, variant = 'segmented', fullWidth = false, ...props }: TabsListProps) {
    return (
        <TabsVariantContext.Provider value={variant}>
            <TabsPrimitive.List
                data-slot='tabs-list'
                data-variant={variant}
                className={cn(
                    'relative flex items-center',
                    variant === 'segmented' && 'gap-1 rounded-md bg-action-segment-track p-1',
                    variant === 'icon' && 'gap-0 rounded-md',
                    fullWidth ? 'w-full *:flex-1' : 'w-fit',
                    'max-w-full overflow-x-auto no-scrollbar',
                    className
                )}
                {...props}
            />
        </TabsVariantContext.Provider>
    )
}

type TabsTriggerProps = Omit<TabsPrimitive.Tab.Props, 'className'> & {
    className?: string
    count?: ReactNode
    icon?: ReactNode
}

function TabsTrigger({ className, count, icon, children, ...props }: TabsTriggerProps) {
    const variant = useContext(TabsVariantContext)

    return (
        <TabsPrimitive.Tab
            data-slot='tabs-trigger'
            className={cn(
                'group/tab inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 font-medium whitespace-nowrap transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-icon-secondary',
                variant === 'segmented' && 'h-9 rounded-sm px-3 text-sm text-action-segment-inactive-foreground hover:bg-surface-raised/60 data-active:bg-action-segment-active data-active:text-action-segment-active-foreground data-active:hover:bg-action-segment-active',
                variant === 'icon' && 'h-12 rounded-md px-3 text-base text-text-primary hover:bg-surface-subtle data-active:bg-linear-to-b data-active:from-surface-muted data-active:to-surface-raised',
                className
            )}
            {...props}
        >
            {icon}
            {children}
            {count !== undefined && (
                <span className='font-normal text-text-secondary group-data-active/tab:text-action-segment-active-foreground'>
                    {count}
                </span>
            )}
        </TabsPrimitive.Tab>
    )
}

function TabsContent({ className, ...props }: Omit<TabsPrimitive.Panel.Props, 'className'> & { className?: string }) {
    return (
        <TabsPrimitive.Panel
            data-slot='tabs-content'
            className={cn('outline-none', className)}
            {...props}
        />
    )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
export type { TabsVariant }
